import styled from '@emotion/styled';
import { useTableContext } from '../../../context/TableContext';
import { Trial } from '../../../models/Trial';
import TableCategoryCell from './TableCategoryCell';
import TableCell from './TableCell';
import Colors from '../../../components/theme/colors';
import React from 'react';
import { useAppContext } from '../../../context/AppContext';
import HorizontalFlex from '../../../components/flex_layouts/HorizontalFlex';
import { css } from '@mui/material';
import { Text7 } from '../../../components/typography/Text7';

interface Props {
  filteredTrials: Trial[];
  itemsPerPage: number;
}

const TableBody = ({ filteredTrials, itemsPerPage, ...props }: Props) => {
  const { trialsCategories, currentPage } = useTableContext();
  const { applyPagination } = useAppContext();

  const getCategoryValue = (key: string) => {
    switch (key) {
      case 'trialName':
        return 'Trial Name';
      case 'pathWay':
        return 'Path Way';
      case 'drug':
        return 'Drug';
      case 'mechanismOfAction':
        return 'Mechanism Of Action';
      case 'trialPhases':
        return 'Trial Phases';
      case 'usLocations':
        return 'US Locations';
      case 'lineOfTherapy':
        return 'Line Of Therapy';
      case 'nctIdentifier':
        return 'NCT Identifier';
      case 'studyDocuments':
        return 'Study Documents';
      case 'internalPriority':
        return 'Internal Priority';
      case 'lastUpdateDate':
        return 'Last Update Date';
    }

    return key;
  };

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  // Slice your array to get the items for the current page
  const itemsToDisplay = applyPagination ? filteredTrials.slice(startIndex, endIndex) : filteredTrials;

  const filteredCategories = trialsCategories.filter(
    (category) => category !== 'pathWay' && category !== 'studyDocuments',
  );

  return (
    <LayoutRoot {...props}>
      <MainContainer numberOfCategories={filteredCategories.length}>
        <table>
          <thead>
            <tr>
              {filteredCategories.map((category) => (
                <th key={category} data-label={category}>
                  <Text7>{getCategoryValue(category)}</Text7>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {itemsToDisplay.map((trial, index) => (
              <Tr index={index}>
                {filteredCategories.map((category) => (
                  <td data-label={category}>
                    <TableCell
                      category={category}
                      key={category}
                      url={category === 'nctIdentifier' ? trial.studyDocuments : null}
                      isLastItem={filteredTrials.length - 1 === index}
                      text={trial[category as keyof Trial]}
                    />
                  </td>
                ))}
              </Tr>
            ))}
          </tbody>
        </table>
      </MainContainer>
    </LayoutRoot>
  );
};

export default TableBody;

const LayoutRoot = styled.div`
  width: 100%;

  @media print {
    min-height: auto;
  }
`;

const MainContainer = styled.div<{ numberOfCategories: number }>`
  background: #fff;
  border-radius: 12px;
  box-shadow: 50px 20px 100px 0 rgba(18, 32, 102, 0.05);
  overflow-y: hidden;
  overflow-x: auto;

  @media print {
    box-shadow: none;
    border: 0;
  }

  @media (max-width: 450px) {
    padding-right: 0;
    border-bottom-right-radius: 0;
    border-top-right-radius: 0;
  }
`;

const Tr = styled.tr<{ index?: number }>``;
